import { Controller } from "@hotwired/stimulus"

// Manages the category-groups settings UI: toggling between the read-only cards
// and the edit/new forms, adding/removing nested category inputs, and triggering
// the (confirmed) group deletion. Mirrors the previous inline jQuery exactly,
// including its document-wide `form` / `.category-group-card` selectors.
export default class extends Controller {
  newGroup(event) {
    event.preventDefault()
    this.#showAllCards()
    document.getElementById("new-category-group-div")?.classList.add("d-none")
    document.getElementById("new-category-group-form")?.classList.remove("d-none")
  }

  cancelNewGroup(event) {
    event.preventDefault()
    document.getElementById("new-category-group-form")?.classList.add("d-none")
    document.getElementById("new-category-group-div")?.classList.remove("d-none")
  }

  editGroup(event) {
    event.preventDefault()
    this.#showAllCards()
    document.getElementById("new-category-group-div")?.classList.remove("d-none")

    const group = event.currentTarget.closest(".category-group")
    group.querySelector(".category-group-card").classList.add("d-none")
    group.querySelector("form").classList.remove("d-none")
  }

  addCategory(event) {
    event.preventDefault()
    const row = event.currentTarget.closest(".row")
    const inputs = row.querySelectorAll(".nested-category-input")
    const lastInput = inputs[inputs.length - 1]

    // The rows can be reordered in the DOM, so neither "the first row carries index
    // [0]" nor "the next free index is the row count" holds: reusing a live index
    // would make Rails update that category instead of creating one.
    const nextIndex = Math.max(...[...inputs].map((input) =>
      Number(input.querySelector(".category-name-input").getAttribute("name").match(/\[(\d+)\]/)[1])
    )) + 1

    const newInput = inputs[0].cloneNode(true)
    newInput.classList.remove("d-none")

    const destroyInput = newInput.querySelector(".destroy-input")
    const nameInput = newInput.querySelector(".category-name-input")
    const positionInput = newInput.querySelector(".position-input")
    destroyInput.value = "false"
    nameInput.value = ""
    positionInput.value = ""
    for (const input of [nameInput, destroyInput, positionInput]) {
      input.setAttribute("name", input.getAttribute("name").replace(/\[\d+\]/, `[${nextIndex}]`))
      input.setAttribute("id", input.getAttribute("id").replace(/_\d+_/, `_${nextIndex}_`))
    }

    lastInput.insertAdjacentElement("afterend", newInput)
    this.#refreshMoveArrows(newInput.closest("form"))
  }

  deleteCategory(event) {
    event.preventDefault()
    const nested = event.currentTarget.closest(".nested-category-input")
    nested.querySelector(".destroy-input").value = "true"
    nested.classList.add("d-none")
    this.#refreshMoveArrows(nested.closest("form"))
  }

  moveCategoryUp(event) {
    event.preventDefault()
    const row = event.currentTarget.closest(".nested-category-input")
    this.#visibleSibling(row, "previousElementSibling")?.insertAdjacentElement("beforebegin", row)
    this.#materializePositions(row.closest("form"))
  }

  moveCategoryDown(event) {
    event.preventDefault()
    const row = event.currentTarget.closest(".nested-category-input")
    this.#visibleSibling(row, "nextElementSibling")?.insertAdjacentElement("afterend", row)
    this.#materializePositions(row.closest("form"))
  }

  deleteGroup(event) {
    event.preventDefault()
    event.currentTarget.closest(".category-group").querySelector(".js-delete-group-form").click()
  }

  // The next .nested-category-input in the given direction, skipping rows hidden
  // because they are marked for destruction.
  #visibleSibling(row, direction) {
    let sibling = row[direction]
    while (sibling && !(sibling.classList.contains("nested-category-input") && !sibling.classList.contains("d-none"))) {
      sibling = sibling[direction]
    }
    return sibling
  }

  // Writes the DOM order into the position inputs. Only called after an actual move:
  // an untouched form keeps its original positions, so never-reordered categories
  // keep sorting alphabetically (same semantics as the families' move!).
  #materializePositions(form) {
    form.querySelectorAll(".nested-category-input").forEach((row, index) => {
      row.querySelector(".position-input").value = index
    })
    this.#refreshMoveArrows(form)
  }

  // No arrow where there is nowhere to go: first visible row loses "up", last "down".
  #refreshMoveArrows(form) {
    const rows = [...form.querySelectorAll(".nested-category-input")].filter((row) => !row.classList.contains("d-none"))
    rows.forEach((row, index) => {
      row.querySelector(".js-move-category-up").classList.toggle("invisible", index === 0)
      row.querySelector(".js-move-category-down").classList.toggle("invisible", index === rows.length - 1)
    })
  }

  // Reveals every group card and hides every form, then re-shows the forms the
  // legacy onboarding page relied on (no-ops on the settings page).
  #showAllCards() {
    document.querySelectorAll(".category-group-card").forEach((el) => el.classList.remove("d-none"))
    document.querySelectorAll("form").forEach((el) => el.classList.add("d-none"))
    document.getElementById("parameters-create-form")?.classList.remove("d-none")
    document.getElementById("skip-onboarding-form")?.closest("form")?.classList.remove("d-none")
  }
}
